import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { EditorContent, Extension, useEditor } from "@tiptap/react";
import React, { useCallback, useEffect } from "react";

import styled from "styled-components/macro";

import { faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import CharacterCount from "@tiptap/extension-character-count";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";
import { motion } from "framer-motion";

// custom extension to prevent new lines on enter
const DisableEnter = Extension.create({
  addKeyboardShortcuts() {
    return {
      Enter: () => true,
    };
  },
});

const Tiptap = ({
  handleUpdate,
  disabled = false,
  initialContent = "",
  autoFocus = false,
}) => {
  const extensions = [
    Document,
    Paragraph,
    Text,
    DisableEnter,
    CharacterCount.configure({ limit: 160 }),
  ];

  const editor = useEditor({
    extensions,
    content: initialContent,
    onUpdate({ editor }) {
      let text = editor.getText({ blockSeparator: "/n" });

      if (text.length > 160) {
        editor.content = text.slice(0, 160);
        return;
      }

      handleUpdate(text);
    },
    editable: !disabled,
    autofocus: autoFocus,
    DisableEnter,
    editorProps: {
      attributes: {
        class: "fundit-memo",
      },
    },
  });

  useEffect(() => {
    /**
     * This allows for resetting the editor value.
     * An example of using it this way is in the recurring payment MemoSection component
     */
    if (!editor?.commands) return;
    editor.commands.setContent(initialContent);
  }, [initialContent, editor]);

  return <EditorContent editor={editor} />;
};

export default function MemoForm({ onRemove, onUpdate, initialContent }) {
  useEffect(
    function clearMemoOnUnmount() {
      return () => onRemove();
    },
    [onRemove]
  );

  return (
    <Wrapper>
      <Label>
        Memo
        <IconButton onClick={onRemove}>
          <FontAwesomeIcon icon={faTimesCircle} color="hsl(207,7%,66%)" />
        </IconButton>
      </Label>
      <Tiptap
        handleUpdate={onUpdate}
        autoFocus={true}
        initialContent={initialContent}
      />
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  box-sizing: border-box;
  width: 100%;
  padding: 10px 14px 14px 14px;
  color: hsl(0, 0%, 30%);
  background-color: hsl(0, 0%, 100%);
  font-weight: 400;
  border-radius: 14px;
  box-shadow: 0 8px 24px 0 #eeeeeeff, 0 4px 8px 0 #42474c0f, 0 0 1px 0 #42474c52;

  p {
    margin: 0;
  }

  .Prose-Mirror {
  }
  .ProseMirror-focused {
    outline: none;
    border-radius: 5px;
  }
`;
const Label = styled.div`
  color: hsl(207, 7%, 66%);
  font-size: 12px;
  text-transform: uppercase;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;
const IconButton = styled.button`
  background: none;
  border: none;
  position: absolute;
  right: -14px;
  top: -7px;
  transition: all 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
    scale: 1.1;
  }
`;
